import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, AlertCircle, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import api from '../lib/api';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSetup, setIsSetup] = useState(false);
  const [adminConfigured, setAdminConfigured] = useState(true);
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('adminToken');
    if (token) {
      navigate('/admin');
    }
    // Check if admin is configured
    checkAdminConfig();
  }, [navigate]);

  const checkAdminConfig = async () => {
    try {
      const response = await api.get('/admin/check');
      setAdminConfigured(response.data.configured);
      setIsSetup(!response.data.configured);
    } catch (error) {
      console.error('Failed to check admin config:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSetup) {
        // Setup new admin
        await api.post('/admin/setup', credentials);
        toast.success('Admin account created! Please login.');
        setIsSetup(false);
        setAdminConfigured(true);
        setCredentials({ username: '', password: '' });
      } else {
        // Login
        const response = await api.post('/admin/login', credentials);
        localStorage.setItem('adminToken', response.data.token);
        toast.success('Login successful!');
        navigate('/admin');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 hero-pattern" data-testid="admin-login-page">
      <div className="w-full max-w-md p-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <Logo className="h-16 w-16 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-white">Bit Buddies</h1>
          <p className="text-slate-400 text-sm mt-2">Admin Panel</p>
        </div>

        {/* Login Card */}
        <div className="glass rounded-xl p-8" data-testid="login-form-container">
          <div className="mb-6">
            <h2 className="font-heading text-xl font-semibold text-white">
              {isSetup ? 'Setup Admin Account' : 'Admin Login'}
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              {isSetup 
                ? 'Create your admin credentials to get started.' 
                : 'Enter your credentials to access the dashboard.'}
            </p>
          </div>

          {!adminConfigured && !isSetup && (
            <div className="flex items-start gap-3 p-4 mb-6 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-amber-400 text-sm font-medium">No admin configured</p>
                <p className="text-amber-400/70 text-xs mt-1">Click below to set up your admin account.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-slate-300">Username</Label>
              <Input
                id="username"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                required
                className="bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white"
                placeholder="admin"
                data-testid="login-username-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  className="bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white pr-10"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-5 mt-6 shadow-[0_0_15px_rgba(6,182,212,0.3)]"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  {isSetup ? 'Creating...' : 'Logging in...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isSetup ? <UserPlus className="h-4 w-4" /> : <LogIn className="h-4 w-4" />}
                  {isSetup ? 'Create Admin' : 'Login'}
                </span>
              )}
            </Button>
          </form>

          {!adminConfigured && (
            <button
              onClick={() => setIsSetup(!isSetup)}
              className="w-full mt-4 text-center text-sm text-slate-400 hover:text-cyan-400 transition-colors"
              data-testid="toggle-setup-btn"
            >
              {isSetup ? 'Back to Login' : 'Setup Admin Account'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
